import React, { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import strings from 'strings';
import { TextBox } from 'UI/components/atoms';
import AutocompleteSelect, {
  statusRenderOption,
  statusStartAdornment
} from 'UI/components/molecules/AutocompleteSelect';
import { Endpoints } from 'UI/constants/endpoints';
import { SendoutsTypes } from 'UI/constants/status';
import { idOptionSelected, REQUIRED_VALIDATION } from 'UI/utils';
import { OptionRenderers, Selectors } from 'UI/utils/renderers';

import { AccountableRecruiterSelector } from '../../shared';

import { getSendoutDetailsEditCandidateRecruiterOptions } from './SendoutDetailsEditDrawer.utils';
import { FORM_FIELDS_MAP } from './SendoutDetailsEditForm.constants';

const COPIES = strings.sendoutsV2.form.sections.candidateDetails;
const DEFAULT_STATUS_OPTIONS_URL = `${Endpoints.SendoutsStatus}?type=${SendoutsTypes.Sendout}`;

const HiddenField = () => null;

export const SendoutDetailsEditForm = ({
  candidate = null,
  jobOrder,
  onStatusSelected = () => {},
  statusOptionsUrl = DEFAULT_STATUS_OPTIONS_URL
}) => {
  const { control, errors, watch } = useFormContext();

  const recruiterOptions = getSendoutDetailsEditCandidateRecruiterOptions(candidate);
  const hiringAuthorities = jobOrder?.hiringAuthorities || [];
  const currentStatus = watch(FORM_FIELDS_MAP.Status.key);

  const handleFieldValueChange = useCallback(([, value]) => value, []);
  const handleStatusChange = useCallback(
    ([, value]) => {
      const nextValue = onStatusSelected(value);
      return nextValue === undefined ? value : nextValue;
    },
    [onStatusSelected]
  );
  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Controller
          name={FORM_FIELDS_MAP.Status.key}
          control={control}
          rules={REQUIRED_VALIDATION}
          defaultValue={null}
          as={
            <AutocompleteSelect
              disableClearable
              error={!!errors[FORM_FIELDS_MAP.Status.key]}
              errorText={errors[FORM_FIELDS_MAP.Status.key]?.message}
              getOptionSelected={idOptionSelected}
              name={FORM_FIELDS_MAP.Status.key}
              placeholder={strings.shared.fields.status}
              renderOption={statusRenderOption}
              startAdornment={
                currentStatus &&
                statusStartAdornment(
                  currentStatus?.style_class_name || currentStatus?.style || currentStatus?.color
                )
              }
              url={statusOptionsUrl}
            />
          }
          onChange={handleStatusChange}
          onChangeName="onSelect"
          valueName="selectedValue"
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={FORM_FIELDS_MAP.EstimatedFeeAmount.key}
          control={control}
          rules={REQUIRED_VALIDATION}
          defaultValue=""
          as={
            <TextBox
              error={!!errors[FORM_FIELDS_MAP.EstimatedFeeAmount.key]}
              errorText={errors[FORM_FIELDS_MAP.EstimatedFeeAmount.key]?.message}
              name={FORM_FIELDS_MAP.EstimatedFeeAmount.key}
              label={COPIES.labels.estimatedFeeAmount}
              inputType="currency"
              placeholder={COPIES.labels.estimatedFeeAmount}
            />
          }
          onChange={handleFieldValueChange}
          onChangeName="onChange"
          valueName="value"
        />
      </Grid>
      <Grid item xs={6}>
        <AccountableRecruiterSelector
          jobOrderId={jobOrder?.id}
          fieldName={FORM_FIELDS_MAP.AccountableRecruiter.key}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={FORM_FIELDS_MAP.CandidateRecruiter.key}
          control={control}
          rules={recruiterOptions.length ? REQUIRED_VALIDATION : { required: false }}
          defaultValue={null}
          as={
            <AutocompleteSelect
              disabled={recruiterOptions.length === 0}
              displayKey="fullName"
              error={!!errors[FORM_FIELDS_MAP.CandidateRecruiter.key]}
              errorText={errors[FORM_FIELDS_MAP.CandidateRecruiter.key]?.message}
              getOptionSelected={Selectors.byId}
              name={FORM_FIELDS_MAP.CandidateRecruiter.key}
              options={recruiterOptions}
              placeholder={COPIES.labels.candidateRecruiter}
            />
          }
          onChange={handleFieldValueChange}
          onChangeName="onSelect"
          valueName="selectedValue"
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name={FORM_FIELDS_MAP.HiringAuthority.key}
          control={control}
          rules={REQUIRED_VALIDATION}
          defaultValue={null}
          as={
            <AutocompleteSelect
              error={!!errors[FORM_FIELDS_MAP.HiringAuthority.key]}
              errorText={errors[FORM_FIELDS_MAP.HiringAuthority.key]?.message}
              placeholder={strings.sendouts.form.labels.hiringAuthority}
              renderOption={OptionRenderers.displayHiringAuthority}
              getOptionSelected={idOptionSelected}
              displayKey="full_name"
              defaultOptions={hiringAuthorities}
            />
          }
          onChange={handleFieldValueChange}
          onChangeName="onSelect"
          valueName="selectedValue"
        />
      </Grid>
      <Controller
        name={FORM_FIELDS_MAP.DeclinationDetails.key}
        control={control}
        defaultValue={null}
        as={HiddenField}
      />
    </Grid>
  );
};
